import React, { useEffect, useRef, useState } from 'react';

import { fetchAddressPlaceDetails, fetchAddressSuggestions } from './restaurantApi';

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

function formatAddressFromDetails(details) {
    if (!details || typeof details !== 'object') return '';
    const parts = [
        details.addressLine1,
        details.addressLine2,
        details.areaName,
        details.city,
        details.state,
        details.postalCode,
        details.country,
    ]
        .map((part) => (part != null ? String(part).trim() : ''))
        .filter(Boolean);
    return parts.join(', ');
}

/**
 * Text input with Google Places suggestions via GET /address/suggest.
 */
export default function AddressSuggestInput({
    id,
    value,
    onChange,
    placeholder = 'Search address…',
    disabled = false,
    className = '',
    inputClassName = '',
    types,
    'aria-label': ariaLabel,
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        const onPointerDown = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setSuggestions([]);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, []);

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        []
    );

    const handleChange = (event) => {
        const next = event.target.value;
        onChange(next);
        setSuggestions([]);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        const trimmed = next.trim();
        if (trimmed.length < 2) {
            setLoading(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            debounceRef.current = null;
            try {
                setLoading(true);
                const list = await fetchAddressSuggestions(
                    trimmed,
                    types ? { types } : {}
                );
                setSuggestions(list);
            } catch (error) {
                console.error('Address suggest failed:', error);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, ADDRESS_SEARCH_DEBOUNCE_MS);
    };

    const handleSelect = async (suggestion) => {
        const placeId = suggestion.place_id ?? suggestion.placeId;
        const description = suggestion.description ?? suggestion.formatted_address ?? '';
        setSuggestions([]);

        if (!placeId) {
            onChange(description);
            return;
        }

        setLoading(true);
        try {
            const details = await fetchAddressPlaceDetails(placeId);
            onChange(formatAddressFromDetails(details) || description);
        } catch (error) {
            console.error('Address details failed:', error);
            onChange(description);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            ref={wrapRef}
            className={`address-suggest-input${className ? ` ${className}` : ''}`}
        >
            <input
                id={id}
                type="text"
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                autoComplete="off"
                disabled={disabled}
                className={inputClassName}
                aria-label={ariaLabel}
                role="combobox"
                aria-expanded={suggestions.length > 0}
                aria-autocomplete="list"
            />
            {loading ? <span className="address-suggest-input__loading">Searching…</span> : null}
            {suggestions.length > 0 ? (
                <ul className="address-suggestions-dropdown" role="listbox">
                    {suggestions.map((suggestion, index) => {
                        const placeId = suggestion.place_id ?? suggestion.placeId;
                        const text =
                            suggestion.description ?? suggestion.formatted_address ?? '';
                        return (
                            <li
                                key={placeId || index}
                                role="option"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelect(suggestion)}
                            >
                                {text}
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}
